
import { useCallback } from 'react';
import { UserCategory } from '@/types';
import { categoryOperationsService } from '@/services/categories/categoryOperations.service';
import { safeToastError, safeToastSuccess } from '@/components/ui/toastManager';
import { networkService } from '@/services/network.service';
import { useCategoryOptimisticUpdates } from '../useCategoryOptimisticUpdates';
import OfflineLogger from '@/utils/offlineLogger';

interface UseCategoryOperationsOptions {
  onCategoryAdded: (category: UserCategory) => void;
  onCategoryUpdated: (id: string, updates: Partial<UserCategory>) => void;
  onCategoryDeleted: (id: string) => void;
  onPendingCountUpdate: () => Promise<void>;
}

export const useCategoryOperations = ({
  onCategoryAdded,
  onCategoryUpdated,
  onCategoryDeleted,
  onPendingCountUpdate
}: UseCategoryOperationsOptions) => {
  const { optimisticUpdateCategory, optimisticAddCategory, optimisticDeleteCategory } = useCategoryOptimisticUpdates();

  const addCategory = useCallback(async (categoryData: Omit<UserCategory, 'id'>): Promise<string | undefined> => {
    try {
      const newCategory = await categoryOperationsService.createCategory({
        name: categoryData.name,
        icon: categoryData.icon,
        color: categoryData.color,
        text_color: categoryData.textColor || '#000000'
      });

      // Aggiornamento ottimistico online
      if (networkService.isOnline()) {
        optimisticAddCategory(newCategory);
      }

      // ✅ OTTIMIZZAZIONE: Solo aggiornamento UI locale, nessun evento che triggera refresh
      onCategoryAdded(newCategory);
      await onPendingCountUpdate();
      
      return newCategory.id;
    } catch (err) {
      OfflineLogger.error('useCategoryOperations.addCategory', err);
      safeToastError('Failed to add category.');
    }
  }, [onCategoryAdded, onPendingCountUpdate, optimisticAddCategory]);

  const createCategory = useCallback(async (categoryData: { name: string; icon: string; color: string; text_color: string }): Promise<UserCategory> => {
    try {
      const newCategory = await categoryOperationsService.createCategory(categoryData);
      
      // Aggiornamento ottimistico online
      if (networkService.isOnline()) {
        optimisticAddCategory(newCategory);
      }
      
      // ✅ OTTIMIZZAZIONE: Solo aggiornamento UI locale, nessun evento che triggera refresh
      onCategoryAdded(newCategory);
      await onPendingCountUpdate();
      
      return newCategory;
    } catch (err) {
      OfflineLogger.error('useCategoryOperations.createCategory', err);
      throw err;
    }
  }, [onCategoryAdded, onPendingCountUpdate, optimisticAddCategory]);

  const updateCategory = useCallback(async (id: string, updates: Partial<UserCategory>): Promise<boolean> => {
    try {
      // Aggiornamento ottimistico online PRIMA dell'operazione
      if (networkService.isOnline()) {
        optimisticUpdateCategory(id, updates);
      }

      const success = await categoryOperationsService.updateCategory(id, updates);
      if (success) {
        // ✅ OTTIMIZZAZIONE: Solo aggiornamento UI locale, nessun evento che triggera refresh
        onCategoryUpdated(id, updates);
        await onPendingCountUpdate();
      }
      return success;
    } catch (err) {
      OfflineLogger.error('useCategoryOperations.updateCategory', err);
      safeToastError('Failed to update category.');
      return false;
    }
  }, [onCategoryUpdated, onPendingCountUpdate, optimisticUpdateCategory]);

  const deleteCategory = useCallback(async (id: string): Promise<boolean> => {
    try {
      // Aggiornamento ottimistico online PRIMA dell'operazione
      if (networkService.isOnline()) {
        optimisticDeleteCategory(id);
      }

      const success = await categoryOperationsService.deleteCategory(id);
      if (success) {
        // ✅ OTTIMIZZAZIONE: Solo aggiornamento UI locale, nessun evento che triggera refresh
        onCategoryDeleted(id);
        await onPendingCountUpdate();
      }
      return success;
    } catch (err) {
      OfflineLogger.error('useCategoryOperations.deleteCategory', err);
      safeToastError('Failed to delete category.');
      return false;
    }
  }, [onCategoryDeleted, onPendingCountUpdate, optimisticDeleteCategory]);

  const batchUpdatePositions = useCallback(async (updates: Array<{ id: string; position: number }>): Promise<boolean> => {
    try {
      const success = await categoryOperationsService.batchUpdatePositions(updates);
      if (success) {
        // Aggiorna immediatamente l'UI tramite callback
        updates.forEach(update => {
          onCategoryUpdated(update.id, { position: update.position });
        });
        await onPendingCountUpdate();
      }
      return success;
    } catch (err) {
      OfflineLogger.error('useCategoryOperations.batchUpdatePositions', err);
      return false;
    }
  }, [onCategoryUpdated, onPendingCountUpdate]);

  return {
    addCategory,
    createCategory,
    updateCategory,
    deleteCategory,
    batchUpdatePositions
  };
};
